import React, { useState, useId } from 'react';
import axios from 'axios';
import { refusalMessage, currentLang } from './docText';
import { useTranslation } from 'react-i18next';
import { GOLD, NAVY_FORM } from './colors';
import { FORM_BACK_BTN, FORM_SAVE_BTN, FORM_SUBMIT_BTN } from './theme';
import { useIsNarrow, gridCols, Labeled } from './ui';

const API = '/api';
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

const DEMO_PHASE1 = {
  passport_country: 'United States',
  passport_number: 'US987654321',
  passport_issue_date: '2021-03-10',
  passport_expiry_date: '2031-03-10',
  ni_number: 'CD789012E',
  start_date: '2025-01-15'
};

const DEMO_PHASE2 = {
  visa_expiry_date: '2027-01-15',
  address: '32 Notting Hill Gate, London W11 3HX',
  address_duration_months: '12',
  personal_email: 'demo.personal@example.com',
  mobile: '+44778901234'
};

const RequiredMark = () => {
  const { t } = useTranslation();
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '2px' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <span style={{ color: '#e74c3c', cursor: 'default' }}>*</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)', backgroundColor: NAVY_FORM, color: '#fff',
          padding: '5px 10px', borderRadius: '4px', fontSize: '12px',
          whiteSpace: 'nowrap', zIndex: 200, pointerEvents: 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }}>
          {t('common.required')}
          <span style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: `5px solid ${NAVY_FORM}`
          }} />
        </span>
      )}
    </span>
  );
};

const Spinner = () => (
  <span style={{
    display: 'inline-block', width: '13px', height: '13px',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff', borderRadius: '50%',
    animation: 'spin 0.65s linear infinite',
    marginRight: '8px', verticalAlign: 'middle'
  }} />
);

function SponsorForm({ employeeId, employeeName, onBack, onComplete, isAO = false, startPhase = 1, requiresVisa = false }) {
  const { t } = useTranslation();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  // RESPONSIVE Phase 2: two-column field rows (styles.row) reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...styles.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [phase, setPhase] = useState(startPhase);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [visaStampFile, setVisaStampFile] = useState(null);

  const [phase1, setPhase1] = useState({ passport_country: '', passport_number: '', passport_issue_date: '', passport_expiry_date: '', ni_number: '', start_date: '' });
  const [phase2, setPhase2] = useState({ visa_expiry_date: '', address: '', address_duration_months: '', personal_email: '', mobile: '' });

  // Phase 3 state
  const [positionAdvertised, setPositionAdvertised] = useState(null);
  const [filledThroughAd, setFilledThroughAd] = useState(null);
  const [recruiterUsed, setRecruiterUsed] = useState(null);
  const [howFilled, setHowFilled] = useState(''); // referral, internal, other
  const [howFilledOther, setHowFilledOther] = useState('');
  const [jobAdFile, setJobAdFile] = useState(null);
  const [recruiterFile, setRecruiterFile] = useState(null);

  const sanitize = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj)) out[k] = v === '' ? null : v;
    return out;
  };

  const formatError = (detail) => {
    if (!detail) return 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg).join('; ');
    return JSON.stringify(detail);
  };

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      if (phase === 1) {
        await axios.post(`${API}/sponsor-form/${employeeId}/phase1?draft=true`, sanitize(phase1));
      } else if (phase === 2) {
        await axios.post(`${API}/sponsor-form/${employeeId}/phase2?draft=true`, {
          ...sanitize(phase2),
          address_duration_months: phase2.address_duration_months ? parseInt(phase2.address_duration_months) : null
        });
      } else if (phase === 3) {
        const how = howFilled === 'other' ? howFilledOther : howFilled;
        await axios.post(`${API}/sponsor-form/${employeeId}/recruitment?draft=true`, {
          position_advertised: positionAdvertised,
          recruiter_used: recruiterUsed || false,
          how_position_filled: how || null
        });
      }
      setSuccess(t('sponsorForm.draftSaved'));
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const submitPhase1 = async () => {
    if (!phase1.passport_country || !phase1.passport_number || !phase1.passport_issue_date || !phase1.passport_expiry_date || !phase1.start_date) {
      setError(t('sponsorForm.errMandatory'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/sponsor-form/${employeeId}/phase1`, phase1);
      setSuccess(t('sponsorForm.successPhase1'));
      setPhase(2);
    } catch (err) {
      setError(err.response?.data?.detail || t('sponsorForm.errMandatory'));
    }
    setLoading(false);
  };

  const submitPhase2 = async () => {
    if (!phase2.visa_expiry_date || !phase2.address || !phase2.address_duration_months) {
      setError(t('sponsorForm.errMandatory'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (visaStampFile) await uploadDoc(visaStampFile, 'visa_stamp');
      await axios.post(`${API}/sponsor-form/${employeeId}/phase2`, {
        ...phase2,
        address_duration_months: parseInt(phase2.address_duration_months)
      });
      setSuccess(t('sponsorForm.successPhase1'));
      if (isAO) {
        setPhase(3);
      } else {
        setSuccess(t('sponsorForm.successPhase2'));
        setTimeout(() => onComplete(), 1500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('sponsorForm.errMandatory'));
    }
    setLoading(false);
  };

  const uploadDoc = async (file, docType) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('employee_id', employeeId);
    formData.append('document_type', docType);
    formData.append('file', file);
    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (err) {
      console.error(`Error uploading ${docType}:`, err);
      // UPLOADREFUSE: this used to fail silently; the server's real reason is now shown.
      setError(refusalMessage(err, currentLang(), 'uploadChecks.uploadFailed'));
    }
  };

  const submitRecruitment = async () => {
    setLoading(true);
    setError(null);
    try {
      if (jobAdFile) await uploadDoc(jobAdFile, 'recruitment_ad');
      if (recruiterFile) await uploadDoc(recruiterFile, 'recruiter_agreement');

      const how = howFilled === 'other' ? howFilledOther : howFilled;

      await axios.post(`${API}/sponsor-form/${employeeId}/recruitment`, {
        position_advertised: positionAdvertised,
        recruiter_used: recruiterUsed || false,
        how_position_filled: how
      });
      setSuccess(t('sponsorForm.successComplete'));
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || t('sponsorForm.errMandatory'));
    }
    setLoading(false);
  };

  // Determine if form is ready to submit
  const canSubmitRecruitment = () => {
    if (positionAdvertised === null) return false;
    if (positionAdvertised === true) {
      if (filledThroughAd === null) return false;
      if (filledThroughAd === true) return true;
      // not filled through ad → same as not advertised
    }
    // not advertised or not filled through ad
    if (recruiterUsed === null) return false;
    if (recruiterUsed === true) return true;
    if (recruiterUsed === false) return howFilled !== '';
    return false;
  };

  const showRecruiterQuestion = positionAdvertised === false || (positionAdvertised === true && filledThroughAd === false);

  const steps = isAO
    ? [t('sponsorForm.preStartDetails'), t('sponsorForm.postStartDetails'), t('sponsorForm.recruitment')]
    : [t('sponsorForm.preStartDetails'), t('sponsorForm.postStartDetails')];

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.header}>
        <h1 style={styles.title}>{t('sponsorForm.title')}</h1>
        <p style={styles.subtitle}>{employeeName}</p>
      </div>

      <div style={styles.progress}>
        {steps.map((label, index) => {
          const stepNum = index + 1;
          const isClickable = phase > stepNum;
          return (
            <div key={index}
              style={{ ...styles.progressItem, cursor: isClickable ? 'pointer' : 'default' }}
              onClick={() => isClickable && setPhase(stepNum)}
              title={isClickable ? `Go back to ${label}` : undefined}>
              <div style={{
                ...styles.progressDot,
                backgroundColor: phase >= stepNum ? NAVY_FORM : '#ddd',
                boxShadow: isClickable ? `0 0 0 2px ${NAVY_FORM}` : 'none'
              }}>
                {stepNum}
              </div>
              <span style={{ ...styles.progressLabel, color: phase >= stepNum ? NAVY_FORM : '#999' }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.successMsg}>{success}</div>}

      {/* PHASE 1 */}
      {phase === 1 && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>{t('sponsorForm.phase1Title')}</h2>
            {isTestAccount() && <button style={styles.demoBtn} onClick={() => setPhase1(DEMO_PHASE1)}>{t('sponsorForm.fillDemo')}</button>}
          </div>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('sponsorForm.passportDetails')}</h3>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f`}>{t('sponsorForm.country')} <RequiredMark /></label>
                
                <input id={`${a11yId}-f`} style={styles.input} value={phase1.passport_country}
                  onChange={e => setPhase1({...phase1, passport_country: e.target.value})} placeholder="e.g. France" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-2`}>{t('sponsorForm.passportNumber')} <RequiredMark /></label>
                
                <input id={`${a11yId}-f-2`} style={styles.input} value={phase1.passport_number}
                  onChange={e => setPhase1({...phase1, passport_number: e.target.value})} placeholder="e.g. FR123456789" />
              </div>
            </div>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-3`}>{t('sponsorForm.issueDate')} <RequiredMark /></label>
                
                <input id={`${a11yId}-f-3`} style={styles.input} type="date" value={phase1.passport_issue_date}
                  onChange={e => setPhase1({...phase1, passport_issue_date: e.target.value})} />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-4`}>{t('sponsorForm.expiryDate')} <RequiredMark /></label>
                
                <input id={`${a11yId}-f-4`} style={styles.input} type="date" value={phase1.passport_expiry_date}
                  onChange={e => setPhase1({...phase1, passport_expiry_date: e.target.value})} />
              </div>
            </div>
          </div>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('sponsorForm.employmentDetails')}</h3>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-5`}>{t('sponsorForm.niNumber')}</label>
                
                <input id={`${a11yId}-f-5`} style={styles.input} value={phase1.ni_number}
                  onChange={e => setPhase1({...phase1, ni_number: e.target.value})} placeholder="e.g. AB123456C" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-6`}>{t('sponsorForm.startDate')} <RequiredMark /></label>
                
                <input id={`${a11yId}-f-6`} style={styles.input} type="date" value={phase1.start_date}
                  onChange={e => setPhase1({...phase1, start_date: e.target.value})} />
              </div>
            </div>
          </div>
          <div style={styles.btnRow}>
            {onBack && (
              <button
                style={styles.backBtnRow}
                onClick={onBack}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
              >
                ← Back to dashboard
              </button>
            )}
            <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
              {saving ? t('sponsorForm.saving') : t('sponsorForm.saveDraft')}
            </button>
            <button style={styles.submitBtn} onClick={submitPhase1} disabled={loading || saving}>
              {loading ? <><Spinner />{t('sponsorForm.submitting')}</> : t('sponsorForm.submitPhase1')}
            </button>
          </div>
        </div>
      )}

      {/* PHASE 2 */}
      {phase === 2 && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>{t('sponsorForm.phase2Title')}</h2>
            {isTestAccount() && <button style={styles.demoBtn} onClick={() => setPhase2(DEMO_PHASE2)}>{t('sponsorForm.fillDemo')}</button>}
          </div>
          <p style={styles.deadline}>{t('sponsorForm.phase2Deadline')}</p>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('sponsorForm.visaDetails')}</h3>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-f-7`}>{t('sponsorForm.visaExpiryDate')} <RequiredMark /></label>
              
              <input id={`${a11yId}-f-7`} style={styles.input} type="date" value={phase2.visa_expiry_date}
                onChange={e => setPhase2({...phase2, visa_expiry_date: e.target.value})} />
            </div>
            {requiresVisa && (
              <div style={styles.field}>
                <label style={styles.label}>{t('sponsorForm.visaStamp')}</label>
                <p style={styles.uploadHint}>{t('sponsorForm.visaStampHint')}</p>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={styles.fileInput}
                  onChange={e => setVisaStampFile(e.target.files[0] || null)} />
                {visaStampFile && <div style={styles.fileSelected}>Selected: {visaStampFile.name}</div>}
              </div>
            )}
          </div>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('sponsorForm.address')}</h3>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-f-8`}>{t('sponsorForm.currentAddress')} <RequiredMark /></label>
              
              <textarea id={`${a11yId}-f-8`} style={styles.textarea} value={phase2.address}
                onChange={e => setPhase2({...phase2, address: e.target.value})} placeholder={t('sponsorForm.addressPlaceholder')} rows={3} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-f-9`}>{t('sponsorForm.lengthOfStay')} <RequiredMark />
                <span style={styles.hint}>{t('sponsorForm.lengthOfStayHint')}</span>
              </label>
              
              <input id={`${a11yId}-f-9`} style={styles.input} type="number" value={phase2.address_duration_months}
                onChange={e => setPhase2({...phase2, address_duration_months: e.target.value})} placeholder="e.g. 12" min="1" />
            </div>
          </div>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('sponsorForm.contactDetails')}</h3>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-10`}>{t('sponsorForm.personalEmail')}</label>
                
                <input id={`${a11yId}-f-10`} style={styles.input} type="email" value={phase2.personal_email}
                  onChange={e => setPhase2({...phase2, personal_email: e.target.value})} placeholder="personal@email.com" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-11`}>{t('sponsorForm.mobile')}</label>
                
                <input id={`${a11yId}-f-11`} style={styles.input} value={phase2.mobile}
                  onChange={e => setPhase2({...phase2, mobile: e.target.value})} placeholder="+44..." />
              </div>
            </div>
          </div>
          <div style={styles.btnRow}>
            {onBack && (
              <button
                style={styles.backBtnRow}
                onClick={onBack}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
              >
                ← Back to dashboard
              </button>
            )}
            <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
              {saving ? t('sponsorForm.saving') : t('sponsorForm.saveDraft')}
            </button>
            <button style={styles.submitBtn} onClick={submitPhase2} disabled={loading || saving}>
              {loading ? <><Spinner />{t('sponsorForm.submitting')}</> : isAO ? t('sponsorForm.submitPhase2') : t('sponsorForm.submitComplete')}
            </button>
          </div>
        </div>
      )}

      {/* PHASE 3 - AO only */}
      {phase === 3 && isAO && (
        <div style={styles.form}>
          <h2 style={styles.formTitle}>{t('sponsorForm.phase3Title')}</h2>
          <p style={styles.deadline}>{t('sponsorForm.phase3Deadline')}</p>

          {/* Q1 - Was position advertised? */}
          <div style={styles.fieldGroup}>
            <div style={styles.field}>
              <label style={styles.label}>{t('sponsorForm.positionAdvertised')} <RequiredMark /></label>
              <div style={styles.radioGroup}>
                <label style={styles.radio}>
                  <input type="radio" name="advertised"
                    onChange={() => { setPositionAdvertised(true); setFilledThroughAd(null); setRecruiterUsed(null); setHowFilled(''); }} /> {t('common.yes')}
                </label>
                <label style={styles.radio}>
                  <input type="radio" name="advertised"
                    onChange={() => { setPositionAdvertised(false); setFilledThroughAd(null); setRecruiterUsed(null); setHowFilled(''); }} /> {t('common.no')}
                </label>
              </div>
            </div>

            {/* If advertised → upload ad + was it filled through the ad? */}
            {positionAdvertised === true && (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>{t('sponsorForm.uploadJobAd')}</label>
                  <p style={styles.uploadHint}>{t('sponsorForm.uploadJobAdHint')}</p>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={styles.fileInput}
                    onChange={e => setJobAdFile(e.target.files[0])} />
                  {jobAdFile && <div style={styles.fileSelected}>Selected: {jobAdFile.name}</div>}
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>{t('sponsorForm.filledThroughAd')} <RequiredMark /></label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radio}>
                      <input type="radio" name="filledThroughAd"
                        onChange={() => { setFilledThroughAd(true); setRecruiterUsed(null); setHowFilled(''); }} /> {t('common.yes')}
                    </label>
                    <label style={styles.radio}>
                      <input type="radio" name="filledThroughAd"
                        onChange={() => { setFilledThroughAd(false); setRecruiterUsed(null); setHowFilled(''); }} /> {t('common.no')}
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* If not filled through ad OR not advertised → recruiter question */}
            {showRecruiterQuestion && (
              <div style={styles.field}>
                <label style={styles.label}>{t('sponsorForm.recruiterUsed')} <RequiredMark /></label>
                <div style={styles.radioGroup}>
                  <label style={styles.radio}>
                    <input type="radio" name="recruiter"
                      onChange={() => { setRecruiterUsed(true); setHowFilled(''); }} /> {t('common.yes')}
                  </label>
                  <label style={styles.radio}>
                    <input type="radio" name="recruiter"
                      onChange={() => { setRecruiterUsed(false); setHowFilled(''); }} /> {t('common.no')}
                  </label>
                </div>
              </div>
            )}

            {/* Recruiter used → upload agreement */}
            {recruiterUsed === true && (
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-f-12`}>{t('sponsorForm.uploadRecruiterAgreement')}</label>
                
                <input id={`${a11yId}-f-12`} type="file" accept=".pdf" style={styles.fileInput}
                  onChange={e => setRecruiterFile(e.target.files[0])} />
                {recruiterFile && <div style={styles.fileSelected}>Selected: {recruiterFile.name}</div>}
              </div>
            )}

            {/* No recruiter → checklist */}
            {showRecruiterQuestion && recruiterUsed === false && (
              <div style={styles.field}>
                <label style={styles.label}>{t('sponsorForm.howFilled')} <RequiredMark /></label>
                <div style={styles.checklistGroup}>
                  <label style={styles.checklistItem}>
                    <input type="radio" name="howFilled"
                      onChange={() => setHowFilled('referral')} />
                    <span>{t('sponsorForm.referral')}</span>
                  </label>
                  <label style={styles.checklistItem}>
                    <input type="radio" name="howFilled"
                      onChange={() => setHowFilled('internal')} />
                    <span>{t('sponsorForm.internal')}</span>
                  </label>
                  <label style={styles.checklistItem}>
                    <input type="radio" name="howFilled"
                      onChange={() => setHowFilled('other')} />
                    <span>{t('sponsorForm.other')}</span>
                  </label>
                </div>
                {howFilled === 'other' && (
                  // LABELFIX: a visible label; the placeholder stays as an example only.
                  <Labeled label={t('sponsorForm.howFilledOtherLabel')} style={{ marginTop: '8px' }}>
                    <input style={styles.input}
                      value={howFilledOther}
                      onChange={e => setHowFilledOther(e.target.value)}
                      placeholder={t('sponsorForm.howFilledOther')} />
                  </Labeled>
                )}
              </div>
            )}
          </div>

          <div style={styles.btnRow}>
            {onBack && (
              <button
                style={styles.backBtnRow}
                onClick={onBack}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
              >
                ← Back to dashboard
              </button>
            )}
            <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
              {saving ? t('sponsorForm.saving') : t('sponsorForm.saveDraft')}
            </button>
            <button
              style={{
                ...styles.submitBtn,
                opacity: canSubmitRecruitment() ? 1 : 0.5,
                cursor: canSubmitRecruitment() ? 'pointer' : 'not-allowed'
              }}
              onClick={submitRecruitment}
              disabled={loading || saving || !canSubmitRecruitment()}
            >
              {loading ? <><Spinner />{t('sponsorForm.submitting')}</> : t('sponsorForm.submitRecruitment')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    maxWidth: '800px', margin: '0 auto', padding: '24px',
    backgroundColor: '#f8f9fa'
  },
  backBtn: {
    background: 'none', border: '1px solid rgba(26,39,68,0.25)', color: NAVY_FORM,
    fontSize: '14px', cursor: 'pointer', padding: '9px 18px',
    borderRadius: '6px', marginBottom: '24px', fontWeight: '600'
  },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  subtitle: { fontSize: '16px', color: '#666', margin: '4px 0 0 0' },
  progress: {
    display: 'flex', gap: '32px', marginBottom: '32px',
    backgroundColor: '#fff', padding: '20px', borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  progressItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  progressDot: {
    width: '28px', height: '28px', borderRadius: '50%', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '600'
  },
  progressLabel: { fontSize: '13px', fontWeight: '500' },
  form: {
    backgroundColor: '#fff', borderRadius: '8px',
    padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  formHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '24px'
  },
  formTitle: { fontSize: '18px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  demoBtn: {
    backgroundColor: '#f0f4ff', color: NAVY_FORM,
    border: '1px solid #c7d4f5', borderRadius: '6px',
    padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '500'
  },
  deadline: {
    backgroundColor: '#fff8e1', border: '1px solid #f39c12',
    borderRadius: '6px', padding: '10px 16px', fontSize: '13px',
    color: '#856404', marginBottom: '24px'
  },
  fieldGroup: { marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #eee' },
  groupTitle: {
    fontSize: '14px', fontWeight: '600', color: '#666',
    textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 16px 0'
  },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' },
  hint: { fontWeight: '400', color: '#888' },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none'
  },
  textarea: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
    outline: 'none', resize: 'vertical'
  },
  radioGroup: { display: 'flex', gap: '24px' },
  radio: { fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  checklistGroup: {
    display: 'flex', flexDirection: 'column', gap: '12px',
    padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px'
  },
  checklistItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '14px', cursor: 'pointer', color: '#333'
  },
  fileInput: {
    width: '100%', padding: '8px', border: '1px dashed #ddd',
    borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
    backgroundColor: '#fafafa', boxSizing: 'border-box'
  },
  uploadHint: { fontSize: '12px', color: '#888', margin: '0 0 8px 0' },
  fileSelected: {
    marginTop: '8px', padding: '8px 12px', backgroundColor: '#e8f8f0',
    border: '1px solid #27ae60', borderRadius: '6px',
    fontSize: '13px', color: '#1a7a45'
  },
  btnRow: { display: 'flex', gap: '12px', marginTop: '8px' },
  // FORMBTNSHARE: shared action-row trio (see theme.js). backBtnRow is the row-context Back sized
  // to match Save/Submit; the page-header backBtn above keeps its own header styling.
  backBtnRow: { ...FORM_BACK_BTN },
  saveBtn: { ...FORM_SAVE_BTN },
  submitBtn: { ...FORM_SUBMIT_BTN },
  error: {
    backgroundColor: '#fde8e8', border: '1px solid #e74c3c',
    borderRadius: '6px', padding: '12px 16px', fontSize: '14px',
    color: '#c0392b', marginBottom: '16px'
  },
  successMsg: {
    backgroundColor: '#e8f8f0', border: '1px solid #27ae60',
    borderRadius: '6px', padding: '12px 16px', fontSize: '14px',
    color: '#1a7a45', marginBottom: '16px'
  }
};

export default SponsorForm;
